import { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { Gauge } from 'lucide-react'
import api from '../services/api'
import MidDayResetCheckInModal from '../components/MidDayResetCheckInModal'
import MidDayResetDashboardNavbar from '../components/MidDayResetDashboardNavbar'
import { getToken, clearAuth } from '../utils/userData'

// Helper to format date as YYYYMMDD integer for session number
const getDateAsSessionNumber = (date = new Date()) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return parseInt(`${year}${month}${day}`)
}

// Helper to parse YYYYMMDD to Date object
const parseSessionNumberToDate = (sessionNumber) => {
  const str = String(sessionNumber)
  const year = parseInt(str.substring(0, 4))
  const month = parseInt(str.substring(4, 6)) - 1 // Months are 0-indexed
  const day = parseInt(str.substring(6, 8))
  return new Date(year, month, day)
}

// Helper to format date for display
const formatDateForDisplay = (date = new Date()) => {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

// Helper to get current date without time
const getToday = () => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return today
}

const scoreLabels = {
  energyLevel: { label: 'Energy Level', emoji: '⚡', color: 'from-yellow-400 to-orange-500' },
  stressLevel: { label: 'Stress Level', emoji: <Gauge className="inline w-5 h-5" />, color: 'from-red-400 to-pink-500' },
  emotionalState: { label: 'Emotional State', emoji: '💜', color: 'from-purple-400 to-indigo-500' },
  physicalState: { label: 'Physical State', emoji: '💪', color: 'from-green-400 to-teal-500' },
}

const MidDayResetDashboard = () => {
  const navigate = useNavigate()
  
  const [loading, setLoading] = useState(true)
  const [userSignup, setUserSignup] = useState(null)
  const [checkIns, setCheckIns] = useState([])
  const [communityStats, setCommunityStats] = useState(null)
  const [checkInModal, setCheckInModal] = useState({ isOpen: false, type: null })
  const [activeTab, setActiveTab] = useState('overview')

  const today = getToday()
  const todaySessionNumber = getDateAsSessionNumber(today)

  useEffect(() => {
    checkAuthAndFetchData()
  }, [])

  const checkAuthAndFetchData = async () => {
    setLoading(true)
    const token = getToken()

    if (token) {
      try {
        const meResponse = await api.get('/api/programs/mid-day-reset/enrollment')
        if (meResponse.data.success) {
          const { user, enrollment } = meResponse.data.data
          setUserSignup({
            id: user.id,
            name: user.name,
            email: user.email,
            ...enrollment?.data,
          })
          fetchCheckIns()
          fetchCommunityStats()
          setLoading(false)
        } else {
          // Not enrolled in the program
          clearAuth()
          navigate('/mid-day-reset')
        }
      } catch (error) {
        console.error('Auth error:', error)
        clearAuth()
        navigate('/mid-day-reset')
      }
    } else {
      // No token - not logged in
      navigate('/mid-day-reset')
    }
  }

  const fetchCheckIns = async () => {
    try {
      const response = await api.get('/api/programs/mid-day-reset/check-ins')
      if (response.data.success) {
        setCheckIns(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching check-ins:', error)
    }
  }

  const fetchCommunityStats = async () => {
    try {
      const response = await api.get('/api/programs/mid-day-reset/stats')
      if (response.data.success) {
        setCommunityStats(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  // Organize check-ins by session number (date)
  const sessionsByDate = useMemo(() => {
    const sessions = {}
    
    checkIns.forEach(checkIn => {
      const key = checkIn.sessionNumber
      if (!sessions[key]) {
        sessions[key] = { pre: null, post: null }
      }
      if (checkIn.checkInType === 'pre-event') {
        sessions[key].pre = checkIn
      } else if (checkIn.checkInType === 'post-event') {
        sessions[key].post = checkIn
      }
    })
    
    // Sort by session number (date) descending
    return Object.entries(sessions)
      .sort((a, b) => parseInt(b[0]) - parseInt(a[0]))
      .map(([sessionNumber, data]) => ({
        sessionNumber: parseInt(sessionNumber),
        date: parseSessionNumberToDate(parseInt(sessionNumber)),
        ...data,
      }))
  }, [checkIns])

  // Stats calculations
  const stats = useMemo(() => {
    const uniqueDays = new Set(checkIns.map(ci => ci.sessionNumber)).size
    const completedSessions = sessionsByDate.filter(s => s.pre && s.post).length
    
    // Calculate average wellness improvement (post vs pre)
    const improvements = sessionsByDate
      .filter(s => s.pre && s.post)
      .map(s => {
        const energyImprove = (s.post.energyLevel || 0) - (s.pre.energyLevel || 0)
        const stressImprove = (s.pre.stressLevel || 0) - (s.post.stressLevel || 0)
        const emotionalImprove = (s.post.emotionalState || 0) - (s.pre.emotionalState || 0)
        const physicalImprove = (s.post.physicalState || 0) - (s.pre.physicalState || 0)
        return (energyImprove + stressImprove + emotionalImprove + physicalImprove) / 4
      })
    
    const avgImprovement = improvements.length > 0
      ? (improvements.reduce((a, b) => a + b, 0) / improvements.length).toFixed(1)
      : 0

    return {
      uniqueDays,
      completedSessions,
      totalCheckIns: checkIns.length,
      avgImprovement,
    }
  }, [checkIns, sessionsByDate])

  const hasPreEventCheckIn = (num) => checkIns.some(
    (ci) => ci.sessionNumber === num && ci.checkInType === 'pre-event'
  )
  const hasPostEventCheckIn = (num) => checkIns.some(
    (ci) => ci.sessionNumber === num && ci.checkInType === 'post-event'
  )

  const openCheckInModal = (type) => {
    setCheckInModal({ isOpen: true, type })
  }

  const closeCheckInModal = () => {
    setCheckInModal({ isOpen: false, type: null })
  }

  const handleCheckInSuccess = () => {
    const token = getToken()
    if (token) {
      fetchCheckIns(token)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-sky-100 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full"
        />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-sky-100">
      {/* Navbar */}
      {userSignup && <MidDayResetDashboardNavbar userSignup={userSignup} />}

      <div className="container mx-auto px-4 py-8">
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Welcome back, {userSignup?.name?.split(' ')[0] || 'Friend'}! 🧘
          </h1>
          <p className="text-gray-600 text-lg">
            Your Mid-Day Reset Dashboard
          </p>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-teal-100">
            <div className="text-3xl mb-2">📅</div>
            <div className="text-3xl font-bold text-teal-600">{stats.uniqueDays}</div>
            <div className="text-sm text-gray-600">Days Attended</div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-cyan-100">
            <div className="text-3xl mb-2">✅</div>
            <div className="text-3xl font-bold text-cyan-600">{stats.completedSessions}</div>
            <div className="text-sm text-gray-600">Full Sessions</div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-sky-100">
            <div className="text-3xl mb-2">📊</div>
            <div className="text-3xl font-bold text-sky-600">{stats.totalCheckIns}</div>
            <div className="text-sm text-gray-600">Total Check-ins</div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-emerald-100">
            <div className="text-3xl mb-2">📈</div>
            <div className="text-3xl font-bold text-emerald-600">+{stats.avgImprovement}</div>
            <div className="text-sm text-gray-600">Avg. Improvement</div>
          </div>
        </motion.div>

        {/* Today's Check-In Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-3xl p-8 shadow-xl border-2 border-teal-200 mb-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-2">🌅</span>
            Today&apos;s Session
          </h2>
          <p className="text-gray-600 mb-6">
            {formatDateForDisplay(today)} • 1:00 PM - 1:30 PM
          </p>

          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => openCheckInModal('pre-event')}
              className={`px-6 py-4 rounded-xl font-semibold transition-all flex items-center gap-2 ${
                hasPreEventCheckIn(todaySessionNumber)
                  ? 'bg-green-100 text-green-700 border-2 border-green-300'
                  : 'bg-teal-600 text-white hover:bg-teal-700'
              }`}
            >
              {hasPreEventCheckIn(todaySessionNumber) ? (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Pre-Session Complete
                </>
              ) : (
                <>
                  <span className="text-xl">🌟</span>
                  Pre-Session Check-In
                </>
              )}
            </button>
            <button
              onClick={() => openCheckInModal('post-event')}
              className={`px-6 py-4 rounded-xl font-semibold transition-all flex items-center gap-2 ${
                hasPostEventCheckIn(todaySessionNumber)
                  ? 'bg-green-100 text-green-700 border-2 border-green-300'
                  : 'bg-cyan-600 text-white hover:bg-cyan-700'
              }`}
            >
              {hasPostEventCheckIn(todaySessionNumber) ? (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Post-Session Complete
                </>
              ) : (
                <>
                  <span className="text-xl">🎉</span>
                  Post-Session Check-In
                </>
              )}
            </button>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'overview'
                ? 'bg-teal-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'history'
                ? 'bg-teal-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            History
          </button>
          <button
            onClick={() => setActiveTab('community')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'community'
                ? 'bg-teal-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            Community
          </button>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="grid md:grid-cols-2 gap-6"
            >
              {/* Wellness Trends */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="mr-2">📈</span>
                  Your Wellness Trends
                </h3>
                {sessionsByDate.filter(s => s.pre && s.post).length > 0 ? (
                  <div className="space-y-4">
                    {Object.entries(scoreLabels).map(([key, { label, emoji }]) => {
                      const sessionsWithBoth = sessionsByDate.filter(s => s.pre && s.post)
                      const avgPre = sessionsWithBoth.reduce((sum, s) => sum + (s.pre[key] || 0), 0) / sessionsWithBoth.length
                      const avgPost = sessionsWithBoth.reduce((sum, s) => sum + (s.post[key] || 0), 0) / sessionsWithBoth.length
                      const improvement = key === 'stressLevel' 
                        ? avgPre - avgPost // Stress: lower is better
                        : avgPost - avgPre // Others: higher is better

                      return (
                        <div key={key} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">{emoji}</span>
                            <span className="text-sm font-medium text-gray-700">{label}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-500">
                              {avgPre.toFixed(1)} → {avgPost.toFixed(1)}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                              improvement > 0
                                ? 'bg-green-100 text-green-700'
                                : improvement < 0
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {improvement > 0 ? '+' : ''}{improvement.toFixed(1)}
                            </span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">
                    Complete a full session (pre & post check-in) to see your wellness trends!
                  </p>
                )}
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="mr-2">🚀</span>
                  Quick Actions
                </h3>
                <div className="space-y-3">
                  <Link
                    to="/mid-day-reset/check-in"
                    className="block w-full px-4 py-3 bg-teal-50 text-teal-700 rounded-xl font-medium hover:bg-teal-100 transition-colors"
                  >
                    📋 Go to Check-In Page
                  </Link>
                  <Link
                    to="/mid-day-reset"
                    className="block w-full px-4 py-3 bg-cyan-50 text-cyan-700 rounded-xl font-medium hover:bg-cyan-100 transition-colors"
                  >
                    ℹ️ About Mid-Day Reset
                  </Link>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'history' && (
            <motion.div
              key="history"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="bg-white rounded-2xl p-6 shadow-lg"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">📅</span>
                Attendance History
              </h3>
              
              {sessionsByDate.length > 0 ? (
                <div className="space-y-4">
                  {sessionsByDate.map(session => (
                    <motion.div
                      key={session.sessionNumber}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border border-gray-200 rounded-xl p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <span className="font-semibold text-gray-900">
                            {formatDateForDisplay(session.date)}
                          </span>
                          {session.pre && session.post && (
                            <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                              Complete ✓
                            </span>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {session.pre && (
                            <span className="px-3 py-1 bg-teal-100 text-teal-700 text-xs rounded-full">
                              Pre ✓
                            </span>
                          )}
                          {session.post && (
                            <span className="px-3 py-1 bg-cyan-100 text-cyan-700 text-xs rounded-full">
                              Post ✓
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {session.pre && session.post && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {Object.entries(scoreLabels).map(([key, { label, emoji }]) => {
                            const diff = key === 'stressLevel'
                              ? (session.pre[key] || 0) - (session.post[key] || 0)
                              : (session.post[key] || 0) - (session.pre[key] || 0)
                            
                            return (
                              <div key={key} className="text-center bg-gray-50 rounded-lg p-2">
                                <div className="text-lg mb-1">{emoji}</div>
                                <div className="text-xs text-gray-500">{label}</div>
                                <div className="flex items-center justify-center gap-1 mt-1">
                                  <span className="text-sm text-gray-600">{session.pre[key]}</span>
                                  <span className="text-gray-400">→</span>
                                  <span className="text-sm font-medium text-gray-800">{session.post[key]}</span>
                                </div>
                                <span className={`text-xs font-bold ${
                                  diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : 'text-gray-500'
                                }`}>
                                  {diff > 0 ? '+' : ''}{diff}
                                </span>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">
                  No check-ins yet. Start by doing today&apos;s session!
                </p>
              )}
            </motion.div>
          )}

          {activeTab === 'community' && (
            <motion.div
              key="community"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="bg-white rounded-2xl p-6 shadow-lg"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">🌐</span>
                Community Stats
              </h3>
              
              {communityStats ? (
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-4xl mb-2">👥</div>
                    <div className="text-3xl font-bold text-teal-600">
                      {communityStats.totalParticipants || 0}
                    </div>
                    <div className="text-sm text-gray-600">Participants</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl mb-2">📊</div>
                    <div className="text-3xl font-bold text-cyan-600">
                      {communityStats.totalCheckIns || 0}
                    </div>
                    <div className="text-sm text-gray-600">Total Check-ins</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl mb-2">⭐</div>
                    <div className="text-3xl font-bold text-sky-600">
                      {communityStats.avgWellnessScore?.toFixed(1) || '0'}
                    </div>
                    <div className="text-sm text-gray-600">Avg Wellness Score</div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">
                  Loading community stats...
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Check-In Modal */}
      {checkInModal.isOpen && (
        <MidDayResetCheckInModal
          isOpen={checkInModal.isOpen}
          onClose={closeCheckInModal}
          checkInDate={today}
          checkInType={checkInModal.type}
          onSuccess={handleCheckInSuccess}
          isAuthenticated={true}
        />
      )}
    </div>
  )
}

export default MidDayResetDashboard
